import type { ChatShareProvider } from "@/src/lib/chat-share/types";

/**
 * Replay buffer so React Strict Mode remounts can re-read after sessionStorage
 * was cleared by a one-shot take on the full-screen editor.
 */
const takenReplayByProvider = new Map<ChatShareProvider, string>();

export function chatShareImportDraftStorageKey(provider: ChatShareProvider) {
  return `chat-share-import-draft:${provider}`;
}

function readChatShareImportDraft(provider: ChatShareProvider): string {
  try {
    const raw = window.sessionStorage.getItem(
      chatShareImportDraftStorageKey(provider),
    );
    return typeof raw === "string" ? raw : "";
  } catch {
    return "";
  }
}

/**
 * Read and clear the draft once (full-screen editor handoff).
 * Remounts within the same visit can replay the taken value.
 */
export function takeChatShareImportDraft(
  provider: ChatShareProvider,
): string {
  const replayed = takenReplayByProvider.get(provider);
  if (replayed !== undefined) {
    return replayed;
  }

  const draft = readChatShareImportDraft(provider);
  clearChatShareImportDraft(provider);
  takenReplayByProvider.set(provider, draft);
  return draft;
}

/**
 * Stash the share URL when opening the full-screen editor from a landing page.
 * Do not call on every keystroke in the import toolbar.
 */
export function writeChatShareImportDraft(
  provider: ChatShareProvider,
  shareUrl: string,
) {
  try {
    const key = chatShareImportDraftStorageKey(provider);
    if (!shareUrl) {
      window.sessionStorage.removeItem(key);
      takenReplayByProvider.delete(provider);
      return;
    }
    window.sessionStorage.setItem(key, shareUrl);
    takenReplayByProvider.delete(provider);
  } catch {
    // sessionStorage may be unavailable (private mode / quota).
  }
}

export function clearChatShareImportDraft(provider: ChatShareProvider) {
  try {
    window.sessionStorage.removeItem(chatShareImportDraftStorageKey(provider));
  } catch {
    // Ignore storage failures when clearing.
  }
}

/** Reset take replay (tests only). */
export function resetChatShareImportDraftReplay(): void {
  takenReplayByProvider.clear();
}
